import React, { useMemo } from "react";
import { SingleSelectInput, Text } from "@ucl/react-library";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useBoundInput } from "src/hooks/use-bound-input";
import { ChallengeDocument, ChallengeFormValues } from "src/types/challenge-form-types";
import { ImportantInformation } from "../important-information/important-information";

type ConfirmDocumentRowProps = {
  document: ChallengeDocument;
  index: number;
  documentTypeOptions: Array<{
    label: string;
    value: string;
  }>;
};

function ConfirmDocumentRow({ document, index, documentTypeOptions }: ConfirmDocumentRowProps) {
  const { t } = useTranslation();
  const { control } = useFormContext<ChallengeFormValues>();

  const documentTypeProps = useBoundInput<ChallengeFormValues>({
    name: `documents.${index}.documentType` as const,
    control,
    rules: {
      required: t("confirmDocuments.errors.documentTypeRequired"),
    },
  });

  return (
    <div data-testid={`confirmDocumentCard-${index}`} className="rounded-2xl border border-primary bg-white p-4">
      <Text.P fontWeight="semi-bold" className="break-all text-primary">
        {document.file.name}
      </Text.P>

      <div className="mt-4">
        <SingleSelectInput
          data-testid={`confirmDocumentTypeSelect-${index}`}
          labelProp={{
            label: t("confirmDocuments.documentTypeLabel"),
          }}
          isSearchable={false}
          options={documentTypeOptions}
          selected={documentTypeProps.value}
          onOptionSelect={(option) => documentTypeProps.onChange(option.value)}
          inputState={documentTypeProps.inputState}
          helperTextProp={documentTypeProps.helperTextProp}
          className="w-full"
        />
      </div>
    </div>
  );
}

export function ConfirmDocuments() {
  const { t } = useTranslation();
  const { watch } = useFormContext<ChallengeFormValues>();

  const documents = watch("documents") ?? [];
  const hasUploadedDocuments = documents.length > 0;

  const documentTypeOptions = useMemo(
    () => [
      {
        label: t("confirmDocuments.options.financeContract"),
        value: "financeContract",
      },
      {
        label: t("confirmDocuments.options.StatementOfAccount"),
        value: "statementOfAccount",
      },
      {
        label: t("confirmDocuments.options.documentFromDealer"),
        value: "documentFromDealer",
      },
      {
        label: t("confirmDocuments.options.somethingElse"),
        value: "somethingElse",
      },
    ],
    [t]
  );


  return (
    <div data-testid="confirmDocuments" className="flex w-full flex-col gap-6">
      {hasUploadedDocuments && (
        <div data-testid="confirmDocumentCards" className="flex flex-col gap-4 pt-6">
          <Text.P size="large">{t("confirmDocuments.subtitleOne")}</Text.P>
          <Text.P size="large">{t("confirmDocuments.subtitleTwo")}</Text.P>
          {documents.map((document, index) => (
            <ConfirmDocumentRow
              key={document.id}
              document={document}
              index={index}
              documentTypeOptions={documentTypeOptions}
            />
          ))}
        </div>
      )}
      <div className="flex flex-col gap-5">
        <Text renderAs="h2" styleType="h3" fontWeight="semi-bold">
          {t("confirmDocuments.importantInformationTitle")}
        </Text>
        {!hasUploadedDocuments && (
          <Text.P size={"large"}>{t("confirmDocuments.noDocumentInfo")}</Text.P>
        )}
        <ImportantInformation showOptInInfo={false} docChallenge />
      </div>
    </div>
  );
}
